// Shared theme-driven assets (updated by ThemeEngine)

// Poster assets (tiles) injected by themes
export type ThemeAssetMap = Record<string, string>
export const ASSETS: ThemeAssetMap = {}

export const HOME_PREVIEW_KEYS = [
  'strategy',
  'puzzle',
  'online',
  'ranking',
  'challenges',
  'tools',
  'arcade',
  'drop',
] as const

export type HomePreviewKey = (typeof HOME_PREVIEW_KEYS)[number]
export type HomePreviewMap = Record<HomePreviewKey, string>

// Safe accessor for poster assets with SVG fallback (works even if a theme is incomplete)
export function assetFor(key: string): string {
  const keyNorm = String(key || '').toLowerCase()

  if (keyNorm === 'blk') {
    const s = 100
    const svgBlk =
      "<svg xmlns='http://www.w3.org/2000/svg' width='" +
      s +
      "' height='" +
      s +
      "' viewBox='0 0 " +
      s +
      ' ' +
      s +
      "'>" +
      '<defs>' +
      "<linearGradient id='bg' x1='0' y1='0' x2='1' y2='1'>" +
      "<stop offset='0' stop-color='#3b4351'/>" +
      "<stop offset='1' stop-color='#1a1f2a'/>" +
      '</linearGradient>' +
      '</defs>' +
      "<rect x='2' y='2' width='" +
      (s - 4) +
      "' height='" +
      (s - 4) +
      "' rx='14' fill='url(#bg)'/>" +
      "<g opacity='0.97'>" +
      "<polygon points='10,10 90,10 50,50' fill='#6f7783' stroke='rgba(0,0,0,0.35)' stroke-width='1.2'/>" +
      "<polygon points='90,10 90,90 50,50' fill='#626a76' stroke='rgba(0,0,0,0.35)' stroke-width='1.2'/>" +
      "<polygon points='90,90 10,90 50,50' fill='#565d69' stroke='rgba(0,0,0,0.35)' stroke-width='1.2'/>" +
      "<polygon points='10,90 10,10 50,50' fill='#666e7a' stroke='rgba(0,0,0,0.35)' stroke-width='1.2'/>" +
      '</g>' +
      "<circle cx='50' cy='50' r='7.5' fill='rgba(0,0,0,0.18)'/>" +
      "<rect x='3' y='3' width='" +
      (s - 6) +
      "' height='" +
      (s - 6) +
      "' rx='13' fill='none' stroke='rgba(255,255,255,0.18)' stroke-width='2'/>" +
      '</svg>'
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svgBlk)
  }

  if (keyNorm === 'abs') {
    const s = 100
    const svgAbs =
      "<svg xmlns='http://www.w3.org/2000/svg' width='" +
      s +
      "' height='" +
      s +
      "' viewBox='0 0 " +
      s +
      ' ' +
      s +
      "'>" +
      '<defs>' +
      "<linearGradient id='gA' x1='0' y1='0' x2='1' y2='1'>" +
      "<stop offset='0' stop-color='#3b3f5a'/>" +
      "<stop offset='1' stop-color='#151824'/>" +
      '</linearGradient>' +
      "<pattern id='pA' patternUnits='userSpaceOnUse' width='10' height='10' patternTransform='rotate(45)'>" +
      "<rect width='10' height='10' fill='none'/>" +
      "<rect x='0' y='0' width='5' height='10' fill='rgba(130,170,255,0.16)'/>" +
      '</pattern>' +
      "<filter id='glowA' x='-50%' y='-50%' width='200%' height='200%'>" +
      "<feGaussianBlur stdDeviation='2.5' result='b'/>" +
      "<feMerge><feMergeNode in='b'/><feMergeNode in='SourceGraphic'/></feMerge>" +
      '</filter>' +
      '</defs>' +
      "<rect x='2' y='2' width='" +
      (s - 4) +
      "' height='" +
      (s - 4) +
      "' rx='12' fill='url(#gA)'/>" +
      "<rect x='2' y='2' width='" +
      (s - 4) +
      "' height='" +
      (s - 4) +
      "' rx='12' fill='url(#pA)' opacity='0.85'/>" +
      "<rect x='3' y='3' width='" +
      (s - 6) +
      "' height='" +
      (s - 6) +
      "' rx='11' fill='none' stroke='rgba(255,255,255,0.18)' stroke-width='2'/>" +
      "<g filter='url(#glowA)' opacity='0.95'>" +
      "<circle cx='50' cy='52' r='16' fill='rgba(130,170,255,0.18)' stroke='rgba(170,210,255,0.55)' stroke-width='2'/>" +
      "<path d='M50 38c7 0 12 6 12 14 0 9-6 16-12 16s-12-7-12-16c0-8 5-14 12-14z' fill='none' stroke='rgba(210,235,255,0.65)' stroke-width='3' stroke-linecap='round'/>" +
      "<path d='M56 44l10-4-4 10' fill='none' stroke='rgba(210,235,255,0.65)' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'/>" +
      '</g>' +
      '</svg>'
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svgAbs)
  }

  try {
    const configuredAsset = ASSETS[keyNorm] || ASSETS[String(key || '')]
    if (configuredAsset) return configuredAsset
  } catch {
    // ignore
  }

  const s = 100
  const label = String(key || '').toUpperCase()
  const svg =
    "<svg xmlns='http://www.w3.org/2000/svg' width='" +
    s +
    "' height='" +
    s +
    "' viewBox='0 0 " +
    s +
    ' ' +
    s +
    "'>" +
    "<rect width='100%' height='100%' rx='8' ry='8' fill='#19202d'/>" +
    "<text x='50' y='56' text-anchor='middle' font-family='system-ui,Arial' font-weight='700' font-size='20' fill='#9aa3b2'>" +
    label +
    '</text>' +
    '</svg>'

  return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
}

// ===== v1.9 — HOME previews (default) =====
export function svgHome(label: string, color = '#7bb1ff'): string {
  const s = 640
  const esc = (str: unknown) =>
    String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
  const svg =
    "<svg xmlns='http://www.w3.org/2000/svg' width='" +
    s +
    "' height='" +
    s +
    "' viewBox='0 0 " +
    s +
    ' ' +
    s +
    "'>" +
    "<defs><linearGradient id='gh' x1='0' y1='0' x2='1' y2='1'><stop offset='0%' stop-color='" +
    (color || '#7bb1ff') +
    "'/><stop offset='100%' stop-color='#222'/></linearGradient></defs>" +
    "<rect width='100%' height='100%' rx='32' ry='32' fill='url(#gh)'/>" +
    "<g fill='white' fill-opacity='.92' font-family='Inter,Arial' text-anchor='middle'>" +
    "<text x='320' y='320' font-size='36' font-weight='900'>" +
    esc(label) +
    '</text>' +
    "<text x='320' y='360' font-size='18' fill-opacity='.7'>Preview</text>" +
    '</g></svg>'
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
}

export const HOME_PREVIEWS: HomePreviewMap = {
  strategy:
    '/api/poster-themes/united-square-original/preview/preview-home-united.png',
  puzzle:
    '/api/poster-themes/united-square-original/preview/preview-home-puzzle.png',
  online:
    '/api/poster-themes/united-square-original/preview/preview-home-online.png',
  ranking:
    '/api/poster-themes/united-square-original/preview/preview-home-ranking.png',
  challenges:
    '/api/poster-themes/united-square-original/preview/preview-home-challenges.png',
  tools:
    '/api/poster-themes/united-square-original/preview/preview-home-tools.png',
  arcade:
    '/api/poster-themes/united-square-original/preview/preview-home-arcade.png',
  // NEW (fallback = SVG inline si aucun asset)
  drop: svgHome('DROP'),
}
